import { useCallback, useEffect, useState } from "react";
import { Plus, Search, SlidersHorizontal } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SelectField, Pager, NONE } from "@/lib/crm";
import { ITV_STATUS, ITV_TYPES } from "@/lib/interventions";
import { PRIORITIES } from "@/components/activities/ActivityDialog";
import { PageHeader, Panel } from "@/components/common/ui-kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { ItvRow, ItvEmpty } from "@/components/interventions/ItvRow";
import { InterventionDialog } from "@/components/interventions/InterventionDialog";
import { SlaPoliciesDialog } from "@/components/interventions/SlaPoliciesDialog";
import { useNavigate } from "react-router-dom";

const CHIPS = [["today", "Oggi", { day: "today", open: true }], ["da_pianificare", "Da pianificare", { status: "da_pianificare" }], ["in_corso", "In corso", { status: "in_corso" }],
  ["sospeso", "Sospesi", { status: "sospeso" }], ["a_rischio", "SLA a rischio", { sla: "a_rischio", open: true }], ["violato", "SLA violati", { sla: "violato", open: true }]];
const opts = (m) => Object.entries(m).map(([k, [l]]) => ({ key: k, label: l }));

export default function InterventionsPage() {
  const { can } = useAuth();
  const nav = useNavigate();
  const tech = can("interventions:execute") && !can("interventions:plan");
  const [chip, setChip] = useState(null);
  const [flt, setFlt] = useState({ status: "", priority: "", type: "", sla: "", q: "", mine: tech, open: true, sort: "scheduled_at" });
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [sum, setSum] = useState(null);
  const [modal, setModal] = useState(null);
  const load = useCallback(() => {
    const c = CHIPS.find((x) => x[0] === chip)?.[2] || {};
    const p = Object.fromEntries(Object.entries({ ...flt, ...c, page }).filter(([, v]) => v !== "" && v !== false && v !== NONE));
    if (p.status) delete p.open;
    api.get("/interventions", { params: p }).then((r) => setData(r.data)).catch((e) => setData({ items: [], total: 0, pages: 1, error: errorMessage(e) }));
    api.get("/interventions/summary").then((r) => setSum(r.data)).catch(() => {});
  }, [flt, chip, page]);
  useEffect(() => { load(); }, [load]);
  const set = (k) => (v) => { setPage(1); setFlt((s) => ({ ...s, [k]: v === NONE ? "" : v })); };
  const canNew = can("interventions:create") || can("interventions:request");
  return (
    <div data-testid="interventions-page" className="pb-20 md:pb-0">
      <PageHeader overline="Operations · Tecnico" title="Interventi" description="Cosa fare, dove, per quale cliente, da chi e quando — con priorità, stato e SLA."
        actions={<>
          {can("interventions:read") && <Button variant="outline" className="gap-2" onClick={() => setModal("sla")} data-testid="itv-sla-policies-button"><SlidersHorizontal />Regole SLA</Button>}
          {canNew && <Button className="gap-2" onClick={() => setModal("new")} data-testid="itv-new-button"><Plus />{can("interventions:create") ? "Nuovo intervento" : "Richiedi intervento"}</Button>}
        </>} />
      <div className="mb-4 flex flex-wrap gap-2" data-testid="itv-chips">
        {CHIPS.map(([k, l]) => (
          <button key={k} onClick={() => { setPage(1); setChip(chip === k ? null : k); }} data-testid={`itv-chip-${k}`}
            className={cn("flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors", chip === k ? "border-brand-500 bg-brand-50 text-brand-700" : "border-line bg-white text-slate-600 hover:bg-slate-50",
              (k === "violato" && sum?.violato) && "text-red-600", (k === "a_rischio" && sum?.a_rischio) && "text-amber-700")}>
            {l}<span className="font-mono">{sum?.[k] ?? "·"}</span>
          </button>
        ))}
      </div>
      <Panel className="p-4 md:p-5">
        <div className="mb-3 grid grid-cols-2 gap-2 md:grid-cols-6">
          <div className="relative col-span-2"><Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" /><Input value={flt.q} onChange={(e) => set("q")(e.target.value)} placeholder="Numero, titolo o cliente" className="pl-9" data-testid="itv-search" /></div>
          <SelectField value={flt.status || NONE} onChange={set("status")} options={opts(ITV_STATUS)} allowNone placeholder="Tutti gli stati" testId="itv-filter-status" />
          <SelectField value={flt.priority || NONE} onChange={set("priority")} options={PRIORITIES} allowNone placeholder="Priorità" testId="itv-filter-priority" />
          <SelectField value={flt.type || NONE} onChange={set("type")} options={ITV_TYPES} allowNone placeholder="Tipologia" testId="itv-filter-type" />
          <SelectField value={flt.sort} onChange={set("sort")} options={[{ key: "scheduled_at", label: "Per data" }, { key: "sla", label: "Per scadenza SLA" }, { key: "priority", label: "Per priorità" }, { key: "created_at", label: "Più recenti" }]} testId="itv-sort" />
        </div>
        <div className="mb-2 flex flex-wrap gap-4 text-xs text-slate-600">
          <label className="flex items-center gap-1.5"><input type="checkbox" checked={flt.open} onChange={(e) => set("open")(e.target.checked)} data-testid="itv-filter-open" />Solo aperti</label>
          {can("interventions:execute") && <label className="flex items-center gap-1.5"><input type="checkbox" checked={flt.mine} onChange={(e) => set("mine")(e.target.checked)} data-testid="itv-filter-mine" />Solo i miei</label>}
        </div>
        {data?.error && <p className="text-sm text-red-600" role="alert">{data.error}</p>}
        {data && !data.items.length && <ItvEmpty />}
        <ul className="divide-y divide-line" data-testid="itv-list">{data?.items.map((i) => <ItvRow key={i.id} itv={i} />)}</ul>
        {data && <Pager page={page} pages={data.pages} total={data.total} onPage={setPage} unit="interventi" />}
      </Panel>
      <InterventionDialog open={modal === "new"} onOpenChange={(o) => !o && setModal(null)} onSaved={(id) => nav(`/interventi/${id}`)} />
      <SlaPoliciesDialog open={modal === "sla"} onOpenChange={(o) => !o && setModal(null)} editable={can("interventions:sla")} />
    </div>
  );
}
